import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusBanner from "../../../shared/components/StatusBanner"
import StatusPill from "../../../shared/components/StatusPill"
import VisualOdontogram from "../../../components/VisualOdontogram"

export default function PatientOdontogramTab({
  isReadOnly,
  readOnlyReason,
  patientOdontogram,
  selectedTooth,
  setSelectedTooth,
  selectedSurface,
  setSelectedSurface,
  newOdontogramCondition,
  setNewOdontogramCondition,
  saveOdontogramEntry,
  sendOdontogramToPlan,
  savingOdontogram,
  formatDateTime
}) {
  const visualEntries =
    patientOdontogram?.allEntries ||
    patientOdontogram?.history ||
    patientOdontogram?.entries ||
    patientOdontogram?.latestEntries ||
    []

  const latestEntriesSource = Array.isArray(patientOdontogram?.latestEntries)
    ? patientOdontogram.latestEntries
    : Array.isArray(visualEntries)
      ? visualEntries
      : []

  const latestEntries = getDisplayedEntries(latestEntriesSource, selectedTooth)

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {isReadOnly ? (
        <StatusBanner
          tone="danger"
          title="Odontograma en solo lectura"
          text={readOnlyReason}
        />
      ) : null}

      <VisualOdontogram
        entries={visualEntries}
        selectedTooth={selectedTooth}
        onSelectTooth={setSelectedTooth}
        selectedSurface={selectedSurface}
        onSelectSurface={setSelectedSurface}
        selectedCondition={newOdontogramCondition}
        onSelectCondition={setNewOdontogramCondition}
        onSave={saveOdontogramEntry}
        onSendToPlan={sendOdontogramToPlan}
        saving={savingOdontogram}
        formatDateTime={formatDateTime}
      />

      <SectionCard title="Listado clínico" compact>
        {!latestEntries.length ? (
          <EmptyStateBlock
            title="Sin registros de odontograma"
            text="Todavía no hay entradas clínicas capturadas para este paciente."
            compact
          />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <StatusPill tone="neutral">Entradas: {latestEntries.length}</StatusPill>

              {selectedTooth != null ? (
                <StatusPill tone="info">Pieza activa: {String(selectedTooth)}</StatusPill>
              ) : null}
            </div>

            <div style={clinicalListWrapStyle}>
              {latestEntries.map((item, index) => (
                <ClinicalEntryCard
                  key={getEntryKey(item, index)}
                  item={item}
                  formatDateTime={formatDateTime}
                  isSelected={String(item?.tooth ?? "") === String(selectedTooth ?? "")}
                />
              ))}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  )
}

function ClinicalEntryCard({ item, formatDateTime, isSelected }) {
  const toothLabel = item?.tooth != null ? String(item.tooth) : "-"
  const surfaceLabel = translateSurfaceForTable(item?.surface, item?.definitionType)
  const conditionLabel = translateCondition(item?.condition)
  const statusLabel = translateEntryStatus(item?.status)
  const statusTone = entryStatusTone(item?.status)
  const createdAtLabel = formatEntryDate(formatDateTime, item?.createdAt)

  return (
    <div
      style={{
        ...entryCardStyle,
        ...(isSelected ? entryCardSelectedStyle : null)
      }}
    >
      <div style={entryMainRowStyle}>
        <div style={entryIdentityStyle}>
          <div
            style={{
              ...toothBadgeStyle,
              ...(isSelected ? toothBadgeSelectedStyle : null)
            }}
          >
            <div style={toothBadgeEyebrowStyle}>Pieza</div>
            <div style={toothBadgeValueStyle}>{toothLabel}</div>
          </div>

          <div style={entryContentStyle}>
            <div style={entryConditionStyle}>{conditionLabel}</div>

            <div style={entryMetaInlineStyle}>
              <span
                style={{
                  ...surfaceChipStyle,
                  ...(isSelected ? surfaceChipSelectedStyle : null)
                }}
              >
                {surfaceLabel}
              </span>

              <span style={entryDotStyle}>•</span>
              <span style={entryDateStyle}>{createdAtLabel}</span>
            </div>
          </div>
        </div>

        {shouldShowEntryStatus(item?.status) ? (
          <StatusPill tone={statusTone}>{statusLabel}</StatusPill>
        ) : null}
      </div>
    </div>
  )
}

const clinicalListWrapStyle = {
  display: "grid",
  gap: 8
}

const entryCardStyle = {
  borderRadius: 14,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  padding: 12
}

const entryCardSelectedStyle = {
  border: "1px solid #bfdbfe",
  background: "#eff6ff"
}

const entryMainRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap"
}

const entryIdentityStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: "1 1 360px",
  minWidth: 0
}

const toothBadgeStyle = {
  width: 58,
  minWidth: 58,
  borderRadius: 14,
  border: "1px solid #dbeafe",
  background: "#f8fafc",
  padding: "8px 6px",
  display: "grid",
  gap: 3,
  justifyItems: "center"
}

const toothBadgeSelectedStyle = {
  border: "1px solid #93c5fd",
  background: "#dbeafe"
}

const toothBadgeEyebrowStyle = {
  fontSize: 10,
  fontWeight: 800,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: 0.25
}

const toothBadgeValueStyle = {
  fontSize: 20,
  lineHeight: 1,
  fontWeight: 900,
  color: "#0f172a"
}

const entryContentStyle = {
  display: "grid",
  gap: 5,
  minWidth: 0,
  flex: 1
}

const entryConditionStyle = {
  fontSize: 15,
  fontWeight: 900,
  color: "#0f172a",
  lineHeight: 1.2,
  wordBreak: "break-word"
}

const entryMetaInlineStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap"
}

const surfaceChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 24,
  padding: "3px 9px",
  borderRadius: 999,
  background: "#eff6ff",
  border: "1px solid #dbeafe",
  color: "#1d4ed8",
  fontSize: 11.5,
  fontWeight: 800
}

const surfaceChipSelectedStyle = {
  background: "#dbeafe",
  border: "1px solid #93c5fd",
  color: "#1d4ed8"
}

const entryDotStyle = {
  color: "#94a3b8",
  fontWeight: 900
}

const entryDateStyle = {
  color: "#475569",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3
}

function getDisplayedEntries(entries, selectedTooth) {
  const rows = Array.isArray(entries) ? [...entries] : []

  return rows.sort((a, b) => {
    const aSelected = String(a?.tooth ?? "") === String(selectedTooth ?? "")
    const bSelected = String(b?.tooth ?? "") === String(selectedTooth ?? "")

    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1

    return safeTimestamp(b?.createdAt) - safeTimestamp(a?.createdAt)
  })
}

function getEntryKey(item, index) {
  return item?.id || `${item?.tooth}-${item?.surface}-${item?.condition}-${index}`
}

function formatEntryDate(formatDateTime, value) {
  if (!value) return "Sin fecha registrada"

  try {
    const raw =
      typeof formatDateTime === "function"
        ? formatDateTime(value)
        : new Date(value).toLocaleString("es-MX")

    if (!raw) return "Sin fecha registrada"

    const text = String(raw)
    if (text.includes("Invalid") || text.includes("NaN")) {
      return "Sin fecha registrada"
    }

    return text
  } catch {
    return "Sin fecha registrada"
  }
}

function safeTimestamp(value) {
  const time = new Date(value || 0).getTime()
  return Number.isFinite(time) ? time : 0
}

function entryStatusTone(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (raw === "ACTIVE" || raw === "ACTIVO") return "success"
  if (raw === "RESOLVED" || raw === "RESUELTO" || raw === "COMPLETED") return "info"
  if (raw === "INACTIVE" || raw === "INACTIVO") return "danger"
  return "neutral"
}

function shouldShowEntryStatus(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (!raw) return false
  if (raw === "ACTIVE" || raw === "ACTIVO") return false

  return true
}

function translateEntryStatus(status) {
  const raw = String(status || "").trim().toUpperCase()

  if (raw === "ACTIVE") return "Activo"
  if (raw === "RESOLVED") return "Resuelto"
  if (raw === "COMPLETED") return "Realizado"
  if (raw === "INACTIVE") return "Inactivo"
  if (!raw) return "Activo"
  return raw
}

function translateSurfaceForTable(surface, definitionType) {
  const raw = String(surface || "").trim().toUpperCase()
  const type = String(definitionType || "").trim().toUpperCase()

  if (!raw || raw === "-" || raw === "ALL") {
    if (type === "TOOTH" || type === "GENERAL" || !raw || raw === "ALL") {
      return "Diente completo"
    }
    return "-"
  }

  if (raw === "M" || raw === "MESIAL") return "Mesial"
  if (raw === "O" || raw === "OCLUSAL" || raw === "OCCLUSAL") return "Oclusal"
  if (raw === "D" || raw === "DISTAL") return "Distal"
  if (raw === "V" || raw === "VESTIBULAR" || raw === "BUCAL" || raw === "LABIAL") return "Vestibular"
  if (raw === "L" || raw === "LINGUAL" || raw === "PALATINO" || raw === "PALATAL") return "Lingual"

  return raw
}

function translateCondition(condition) {
  const raw = String(condition || "").trim().toUpperCase()

  if (raw === "CARIES") return "Caries"
  if (raw === "RESTAURACION" || raw === "RESTORATION") return "Restauración"
  if (raw === "RESTAURACION_DEFECTUOSA" || raw === "DEFECTIVE_RESTORATION") return "Restauración defectuosa"
  if (raw === "ENDODONCIA" || raw === "ENDODONTIC") return "Endodoncia"
  if (raw === "CORONA" || raw === "CROWN") return "Corona"
  if (raw === "IMPLANTE" || raw === "IMPLANT") return "Implante"
  if (raw === "AUSENTE" || raw === "MISSING") return "Ausente"
  if (raw === "EXTRAIDO" || raw === "EXTRACTED") return "Extraído"
  if (raw === "FRACTURA" || raw === "FRACTURE") return "Fractura"
  if (raw === "MOVILIDAD" || raw === "MOBILITY") return "Movilidad"
  if (raw === "DESGASTE" || raw === "WEAR") return "Desgaste"
  if (raw === "PROTESIS" || raw === "PROSTHESIS") return "Prótesis"
  if (raw === "OBSERVACION" || raw === "OBSERVATION") return "Observación"
  if (raw === "LESION") return "Lesión"
  if (raw === "SELLADOR" || raw === "SEALANT") return "Sellador"
  if (raw === "SANO" || raw === "HEALTHY") return "Sano"

  if (!raw) return "-"
  return raw
}
